import type { Notification } from '@talkvibe/shared';

import { translate } from '../i18n/index.ts';

/**
 * What a notification says, and where it goes when it is opened.
 *
 * Both used to live in `Notifications.vue`, which was the only thing that drew
 * one. A browser notification is a second renderer of the same row, and two
 * copies of this wording would drift the first time a type was added — the
 * popup would say one thing and the list another about the same event.
 */

/** One line, no message text: this is read on a lock screen. */
export function describeNotification(notification: Notification): string {
  const who = notification.actor?.displayName ?? translate('notifications.someone');
  switch (notification.type) {
    case 'friend_request':
      return translate('notifications.friendRequest', { name: who });
    case 'friend_request_accepted':
      return translate('notifications.friendAccepted', { name: who });
    case 'new_message':
      return translate('notifications.newMessage', { name: who });
    case 'group_post':
      return translate('notifications.groupPost', { name: who });
    case 'post_mention':
      return translate('notifications.postMention', { name: who });
    case 'profile_post':
      return translate('notifications.profilePost', { name: who });
    case 'post_repost':
      return translate('notifications.postRepost', { name: who });
    case 'story_mention':
      return translate('notifications.storyMention', { name: who });
    default:
      return translate('notifications.title');
  }
}

/**
 * Where it takes you: the request queue, the group, or a profile.
 *
 * A tag falls through to the profile of whoever did it, because a single post
 * still has no page of its own — `/p/:slug/posts/:postId` renders the page it
 * is on, and a profile post is on nobody's page.
 *
 * `viewerUsername` is only needed by `profile_post`, which goes to the reader's
 * *own* profile — the notification names the person who wrote the note, not the
 * wall it is on, because the wall is always yours. Passed in rather than read
 * from the auth store, which would make this module import a store and give the
 * popup a reason to care about who is signed in.
 */
export function notificationDestination(
  notification: Notification,
  viewerUsername?: string,
): string {
  switch (notification.type) {
    case 'friend_request':
      return '/friends';
    case 'new_message':
      return '/messages';
    case 'group_post':
      // `contextId` names the group. It is carried on the notification rather
      // than looked up, so this still works after the post itself is removed.
      return notification.contextId ? `/groups/${notification.contextId}` : '/notifications';
    case 'profile_post':
      // Your own profile: that is where the note is, and the only place it can
      // be read in the context it was written in.
      return viewerUsername ? `/@${viewerUsername}` : '/notifications';
    default:
      return notification.actor ? `/@${notification.actor.username}` : '/notifications';
  }
}

/**
 * What a repeat of this notification should replace.
 *
 * A 1:1 message arrives on the socket twice — as a `new_message` notification
 * and as conversation activity — so both tag by the message id and the second
 * popup silently replaces the first instead of adding to it. Everything else
 * collapses by kind: five people accepting a friend request is one popup that
 * updates, not a column of five.
 */
export function notificationTag(notification: Notification): string {
  return notification.type === 'new_message' && notification.referenceId
    ? `message:${notification.referenceId}`
    : `notification:${notification.type}`;
}
